import {
  Alert,
  Button,
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  Input,
  StatusPill,
  WeightChart,
  cn,
} from "@breenova/ui";
import { Activity, ArrowDownRight, ArrowRight, ArrowUpRight, Scale } from "lucide-react";
import { useState } from "react";

import { useAddWeight, useHealthTimeline, type TimelineEntry } from "@/lib/health";

/** Icon per timeline source, so the feed is scannable without reading every line. */
const ENTRY_ICONS = {
  Health: Activity,
  Weight: Scale,
  Molt: Activity,
  Movement: ArrowRight,
  Mortality: Activity,
} as const;

export function HealthTab({
  birdId,
  bare = false,
}: {
  birdId: string;
  /** Inside a record that already pads its own column. */
  bare?: boolean;
}) {
  const timeline = useHealthTimeline(birdId);
  const addWeight = useAddWeight(birdId);
  const [grams, setGrams] = useState("");
  const [error, setError] = useState<string | null>(null);

  if (timeline.isPending) {
    return <p className="p-lg text-body text-on-surface-variant">Loading health…</p>;
  }

  if (timeline.isError || !timeline.data) {
    return (
      <div className="p-lg">
        <Alert tone="error">Could not load this bird's health record.</Alert>
      </div>
    );
  }

  const { withdrawal, weight, entries } = timeline.data;

  async function record(): Promise<void> {
    setError(null);
    const value = Number(grams);

    if (!Number.isFinite(value) || value <= 0) {
      setError("Enter a weight in grams.");
      return;
    }

    try {
      await addWeight.mutateAsync({ grams: value });
      setGrams("");
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : "Could not record the weight.");
    }
  }

  // Flags the most recent point when the loss is worth acting on. The index is the last
  // one because the summary compares the two most recent readings.
  const chartPoints = (timeline.data.entries ?? [])
    .filter((entry) => entry.kind === "Weight")
    .map((entry) => ({ on: entry.on, grams: Number.parseFloat(entry.title) }))
    .filter((point) => Number.isFinite(point.grams))
    .reverse();

  return (
    <div className={cn("flex flex-col gap-md", bare ? "" : "px-5 pb-lg md:px-gutter-wide")}>
      {/* The warning that has to be impossible to miss: showing or selling a bird
          inside withdrawal breaches most federations' rules. */}
      {withdrawal.isActive ? (
        <Alert tone="error">
          <span className="font-medium">Under medication withdrawal.</span>{" "}
          {withdrawal.medication ? `${withdrawal.medication}. ` : ""}
          {withdrawal.endsOn
            ? `Not to be shown, sold or bred from until ${withdrawal.endsOn} (${withdrawal.daysRemaining} days).`
            : "The course has not been closed, so the clear date is not known yet."}
        </Alert>
      ) : null}

      <Card>
        <CardHeader className="flex-row items-center justify-between">
          <CardTitle>Weight</CardTitle>
          {weight.isSignificantLoss ? (
            <StatusPill tone="critical">
              {/* Status colour never carries meaning alone: the pill has a label and
                  the number beside it says how much. */}
              Losing condition
            </StatusPill>
          ) : null}
        </CardHeader>

        <CardContent className="flex flex-col gap-md">
          <div className="flex flex-wrap items-baseline gap-md" data-numeric>
            <span className="text-page-title text-on-surface">
              {weight.latestGrams !== null && weight.latestGrams !== undefined
                ? `${weight.latestGrams} g`
                : "—"}
            </span>

            {weight.changeGrams !== null && weight.changeGrams !== undefined ? (
              <span
                className={`inline-flex items-center gap-xs text-body ${
                  weight.isSignificantLoss ? "text-error" : "text-on-surface-variant"
                }`}
              >
                {weight.direction === "Gaining" ? (
                  <ArrowUpRight className="size-4" aria-hidden />
                ) : weight.direction === "Losing" ? (
                  <ArrowDownRight className="size-4" aria-hidden />
                ) : null}
                {weight.changeGrams > 0 ? "+" : ""}
                {weight.changeGrams} g
                {weight.changePercent !== null && weight.changePercent !== undefined
                  ? ` (${Math.round(weight.changePercent * 100)}%)`
                  : ""}
              </span>
            ) : null}
          </div>

          <WeightChart
            points={chartPoints}
            title="Weight over time, grams"
            alertIndex={weight.isSignificantLoss ? chartPoints.length - 1 : undefined}
            alertLabel={
              weight.changePercent !== null && weight.changePercent !== undefined
                ? `${Math.round(weight.changePercent * 100)}%`
                : undefined
            }
          />

          <div className="flex items-end gap-sm">
            <Input
              type="number"
              step="0.1"
              min={0}
              inputMode="decimal"
              aria-label="Weight in grams"
              placeholder="Weigh in grams"
              data-numeric
              value={grams}
              onChange={(event) => setGrams(event.target.value)}
            />
            <Button variant="secondary" disabled={addWeight.isPending} onClick={() => void record()}>
              {addWeight.isPending ? "Saving…" : "Record"}
            </Button>
          </div>

          {error ? <Alert tone="error">{error}</Alert> : null}
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Timeline</CardTitle>
        </CardHeader>
        <CardContent>
          {entries.length === 0 ? (
            <p className="text-body text-on-surface-variant">Nothing recorded yet.</p>
          ) : (
            <ul className="flex flex-col">
              {entries.map((entry, index) => (
                <li key={`${entry.kind}-${entry.sourceId}-${index}`}>
                  <TimelineRow entry={entry} />
                </li>
              ))}
            </ul>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

function TimelineRow({ entry }: { entry: TimelineEntry }) {
  const Icon = ENTRY_ICONS[entry.kind as keyof typeof ENTRY_ICONS] ?? Activity;

  return (
    <div className="flex gap-md border-b border-outline-variant py-sm last:border-b-0">
      <Icon className="mt-xs size-4 shrink-0 text-on-surface-variant" aria-hidden />

      <div className="flex min-w-0 flex-1 flex-col gap-xs">
        <div className="flex flex-wrap items-baseline justify-between gap-sm">
          <span className="text-data text-on-surface" data-numeric>
            {entry.title}
          </span>
          <span className="shrink-0 text-label-caps uppercase text-on-surface-variant" data-numeric>
            {entry.on}
          </span>
        </div>

        {entry.detail ? (
          <span className="text-body text-on-surface-variant">{entry.detail}</span>
        ) : null}

        {/* Worth distinguishing: "everything in flight B was wormed" reads differently
            from "this bird was wormed". */}
        {entry.viaLocation ? (
          <span className="text-label-caps uppercase text-on-surface-variant">
            Via cage treatment
          </span>
        ) : null}
      </div>
    </div>
  );
}
